<template>
  <div :style="{ height: height }" class="map-wrapper" v-bind:class="{addition: addition}">
    <l-map
      :style="mapStyle"
      :zoom="zoom"
      :center="center"
      :options="{ attributionControl: false }"
      @update:zoom="zoomUpdated"
      @update:center="centerUpdated"
      @update:bounds="boundsUpdated"
      @click="addMarker"
    >
      <l-tile-layer :url="url" />
      <v-geosearch :options="geosearchOptions" />
      <div v-if="single">
        <l-marker
          v-if="marker.coordinates"
          :draggable="markerDraggable"
          :lat-lng="marker.coordinates"
          @drag="onMarkerDrag($event)"
          @dragend="onMarkerDragEnd(marker)"
          @click="onMakerClicked(marker)"
        />
        <l-circle 
          :lat-lng="marker.coordinates"
          :radius="radius"
          :color="color"
          v-if="marker.coordinates"
         />
      </div>
      <div v-else>
        <l-marker-cluster :options="clusterOptions">
            <l-marker 
              v-for="(imarker, index) in markers" 
              :key="index" 
              :lat-lng="imarker.coordinates"
              :draggable="markerDraggable"
              :opacity="!highlightRegionId || Number(highlightRegionId) === Number(imarker.region_id) ? 1 : 0.3"
              @drag="onMarkerDrag($event, index)"
              @dragend="onMarkerDragEnd(imarker)"
              @click="onMakerClicked(imarker)"
              :icon="getMarkerIcon(imarker)"
              :title="imarker.tooltip"
              :options="{ imarker }"
            >
            <slot name="marker-tooltip" v-bind:marker="imarker">
              <l-tooltip v-if="imarker.tooltip">{{imarker.tooltip}}</l-tooltip>
            </slot>
          </l-marker>
          <l-tooltip v-slot="cluster">
            {{ getClusterTooltip(cluster) }}
          </l-tooltip>
        </l-marker-cluster>

        <l-circle v-for="(imarker, index) in markers"
          :key="index + 99999"
          :visible="!highlightRegionId || highlightRegionId === imarker.id"
          :lat-lng="imarker.coordinates"
          :radius="parseFloat(imarker.radius|| 4000)"
          :color="color"
          :interactive="true"
        />
      </div>
      <l-control position="bottomright" v-show="!readOnly">
        <base-button type="secondary" v-bind:class="{ active: addition }" @click="activateMarker">
          <i class="fa fa-map-pin"></i>
        </base-button>
      </l-control>
      <l-control position="topright" >
        <base-button class="no-padding" type="secondary" @click="toogleFullscreen">
          <i class="fas fa-expand fa-2x" />
        </base-button>
      </l-control>
    </l-map>
  </div>
</template>
<script>
  import "leaflet.markercluster"; // Importação necessária para clustering
  import "leaflet.markercluster/dist/MarkerCluster.css";
  import "leaflet.markercluster/dist/MarkerCluster.Default.css";
  import LMarkerCluster from "vue2-leaflet-markercluster";
  import { LMap, LTileLayer, LMarker, LCircle, LControl, LTooltip } from 'vue2-leaflet';
  import { OpenStreetMapProvider } from 'leaflet-geosearch';
  import VGeosearch from 'vue2-leaflet-geosearch';

  export default {
    name: 'region-map',
    components: {
      LMap,
      LTileLayer,
      LMarker,
      LCircle,
      LControl,
      VGeosearch,
      LTooltip,
      LMarkerCluster,
    },
    props: {
      single: {
        type: Boolean,
        default: function() {
          return false
        },
      },
      url: {
        type: String,
        default: function() {
          return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
        }
      },
      height: {
        type: String,
        default: function() {
          return '300px';
        },
      },
      readOnly: {
        type: Boolean,
        default: function() {
          return false
        },
      },
      markers: {
        type: Array,
        default: function(){
          return [];
        }
      },
      marker: Object,
      zoom: {
        type: Number,
        default: function() {
          return 4;
        }
      },
      mapStyle: Object,
      center: {
        type: Array,
        default: function(){
          return [-14.235004, -51.92528]
        }
      },
      markerDraggable: {
        type: Boolean,
        default: () => false,
      },
      geosearchOptions: {
        type: Object,
        default: function() {
          return { // Important part Here
            provider: new OpenStreetMapProvider({
              params: {
                countrycodes: 'br',
                addressdetails: 1,
                'accept-language': 'br',
              }
            }),
            showMarker: false,
            autoClose: true,
            searchLabel: "Digite o Endereço",
            notFoundMessage: "Endereço não encontrado",
            keepResult: true,
            retainZoomLevel: false,
            style: window.innerWidth < 1200 ? null : 'bar',
            maxSuggestions: 0,
            autoComplete: true,
            autoCompleteDelay: 200,
          };
        }
      },
      radiusOptions: {
        type: Object,
        default: function() {
          return {
            radius: 4500,
            color: 'blue',
          }
        }
      },
      radius: {
        type: Number,
        default: function() {
          return 4500;
        }
      },
      color: {
        type: String,
        default: function(){
          return 'blue'
        }
      },
      bounds: {
        type: Array,
        default: function(){
          return null;
        }
      },
      highlightRegionId: {
        type: Number,
        default: () => null,
      }
    },
    data: function() {
      return {
        addition: false,
        map: {
          zoom: this.zoom,
          center: this.center,
          bounds: this.bounds
        },
        activeMarkers: this.markers,
        interactive: true,
        highlightedClusters: new Set(), // Armazena os clusters temporários destacados
        clusterOptions: {
          iconCreateFunction: (cluster) => {
            let count = cluster.getChildCount();
            let markers = cluster.getAllChildMarkers();

            // Pegando os nomes das regiões dentro do cluster
            let regionNames = markers
              .map(marker => marker.options && marker.options.imarker && marker.options.imarker.tooltip 
                ? marker.options.imarker.tooltip 
                : "Região Desconhecida")
              .join(", ");

            return L.divIcon({
              html: `
                <div class="custom-cluster">${count}</div>
                <div class="custom-cluster-tooltip">${regionNames}</div>
              `,
              className: "custom-cluster-icon",
              iconSize: [50, 50],
            });
          },
        },
        
      }
    },
    methods: {
      getClusterTooltip(cluster) {
        const clusterNames = cluster.getAllChildMarkers()
          .map(m => m.options.tooltip || "Região Desconhecida")
          .join(", ");
        return clusterNames;
      },
      getMarkerIcon(imarker) {
        const isHighlighted = this.highlightRegionId === imarker.region_id;
        return L.icon({
          iconUrl: isHighlighted
            ? "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png"
            : "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
          shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });
      },
      zoomUpdated (zoom) {
        this.$emit('update:zoom', zoom)
      },
      centerUpdated (center) {
        this.$emit('update:center', center)
      },
      boundsUpdated (bounds) {
        this.$emit('update:bounds', bounds)
      },
      activateMarker(){
        this.addition = !this.addition
      },
      addMarker(event){
        if (!this.addition)
          return false
        
        if (this.single){
          this.$emit('update:marker', {coordinates: [event.latlng.lat, event.latlng.lng] })

        } else {
          let tempMarkers = this.markers
          tempMarkers.push({coordinates: [event.latlng.lat, event.latlng.lng] } )
          this.$emit('update:markers', tempMarkers)
        }        
      },
      handleResultSelected(result) {
        // Extract the coordinates from the selected result
        const coordinates = [result.y, result.x];

        // Update the map center and markers based on the selected result
        if (this.single) {
          this.$emit('update:marker', { coordinates });
        } else {
          const tempMarkers = this.markers.slice(); // Create a copy of markers array
          tempMarkers.push({ coordinates });
          this.$emit('update:markers', tempMarkers);
        }

        // Optionally, you can also pan the map to the selected location
        this.center = coordinates;
      },
      onMarkerDrag(event, index = 0){
        if (this.single){
          this.marker.coordinates = [event.latlng.lat, event.latlng.lng]
        } else {
          let tempMarkers = this.markers
          tempMarkers[index].coordinates = [event.latlng.lat, event.latlng.lng]
        }
      },
      onMarkerDragEnd(marker){
        this.$emit('update:marker', {...marker})
      },
      onMakerClicked(marker){
        this.$emit('marker-clicked', marker);
      },
      toogleFullscreen(){
        var elem = document.getElementsByClassName("map-wrapper")[0];
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.mozRequestFullScreen) { /* Firefox */
          elem.mozRequestFullScreen();
        } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE/Edge */
          elem.msRequestFullscreen();
        }
      },
    }
  }
</script>
<style scoped lang="scss">
  .addition{ 
    .leaflet-grab{
      cursor: url("~leaflet/dist/images/marker-icon.png") 11 42, auto;
    }
  }

  .leaflet-control{
    i{
      color: #2dce89;
    }
  }

  .no-padding{
    padding: 2px;
    i {
      color: #000;
    }
  }

</style>
<style scoped lang="css"> 
  @import url("https://unpkg.com/leaflet-geosearch@2.6.0/assets/css/leaflet.css");
  
  .leaflet-control-geosearch.bar form {
    position: relative;
    top: 0;
    left: 0;
    display: block;
    border: 2px solid rgba(0, 0, 0, 0.3);
    border-radius: 0.375rem !important;
  }
  .vue2leaflet-map {
    height: 100%;
    width: 100%;
  }
</style>

<style>
.leaflet-container {
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
}

.leaflet-tile-container img.leaflet-tile {
  filter: saturate(1.3) contrast(1.25) hue-rotate(15deg) brightness(0.98);
}
.custom-cluster-icon {
  background-color: rgba(0, 82, 204, 0.8); /* Azul similar ao marcador */
  border-radius: 50%;
  color: white;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-cluster {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease-in-out;
}

.custom-cluster-tooltip {
  background: rgba(0, 82, 204, 0.9);
  color: white;
  padding: 5px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: bold;
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  display: none;
}

.custom-cluster-icon:hover .custom-cluster-tooltip {
  display: block;
}

</style>
